import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { coachingAPI } from "@/lib/api";
import { TrendingUp, Loader2, Target, AlertTriangle, CheckCircle, MapPin, ClipboardList } from "lucide-react";
import { toast } from "sonner";

const PROFESSIONS = [
  "Doctor", "Nurse", "Physiotherapist", "Occupational Therapist",
  "Speech & Language Therapist", "Radiographer", "Pharmacist",
  "Paramedic", "Biomedical Scientist", "Dietitian", "Podiatrist", "Psychologist", "Other"
];
const BANDS = ["Band 4", "Band 5", "Band 6", "Band 7", "Band 8a", "Band 8b", "Band 8c", "Band 8d"];

export default function BandCoaching() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    current_band: "", target_band: "", profession: "",
    current_responsibilities: "", leadership_exposure: "",
    governance_involvement: "", research_audit: "", cpd_history: "",
  });

  const updateForm = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleGenerate = async () => {
    if (!form.current_band || !form.target_band || !form.profession) {
      toast.error("Please select profession, current band, and target band");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await coachingAPI.generate(form);
      setResult(res.data);
      toast.success("Coaching plan generated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="band-coaching-page">
        <div>
          <h1 className="text-2xl font-bold text-[#003087]" style={{ fontFamily: "Public Sans" }}>Band Progression Coach</h1>
          <p className="text-slate-500 mt-1">Get personalised AI mentoring for your next band progression.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Form */}
          <div className="space-y-4">
            <Card className="bg-white border border-slate-200 shadow-sm rounded-xl">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Your Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Profession *</Label>
                  <Select value={form.profession} onValueChange={(v) => updateForm("profession", v)}>
                    <SelectTrigger data-testid="coaching-profession-select"><SelectValue placeholder="Select profession" /></SelectTrigger>
                    <SelectContent>{PROFESSIONS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Current Band *</Label>
                    <Select value={form.current_band} onValueChange={(v) => updateForm("current_band", v)}>
                      <SelectTrigger data-testid="coaching-current-band"><SelectValue placeholder="Current" /></SelectTrigger>
                      <SelectContent>{BANDS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Target Band *</Label>
                    <Select value={form.target_band} onValueChange={(v) => updateForm("target_band", v)}>
                      <SelectTrigger data-testid="coaching-target-band"><SelectValue placeholder="Target" /></SelectTrigger>
                      <SelectContent>{BANDS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Current Responsibilities</Label>
                  <Textarea placeholder="Describe your current role responsibilities..." className="min-h-[80px]" value={form.current_responsibilities} onChange={(e) => updateForm("current_responsibilities", e.target.value)} data-testid="coaching-responsibilities" />
                </div>
                <div className="space-y-2">
                  <Label>Leadership Exposure</Label>
                  <Textarea placeholder="Leadership, mentoring, management experience..." className="min-h-[80px]" value={form.leadership_exposure} onChange={(e) => updateForm("leadership_exposure", e.target.value)} data-testid="coaching-leadership" />
                </div>
                <div className="space-y-2">
                  <Label>Governance Involvement</Label>
                  <Textarea placeholder="Quality improvement, governance activities..." className="min-h-[60px]" value={form.governance_involvement} onChange={(e) => updateForm("governance_involvement", e.target.value)} data-testid="coaching-governance" />
                </div>
                <div className="space-y-2">
                  <Label>Research/Audit</Label>
                  <Textarea placeholder="Research or audit participation..." className="min-h-[60px]" value={form.research_audit} onChange={(e) => updateForm("research_audit", e.target.value)} data-testid="coaching-research" />
                </div>
                <div className="space-y-2">
                  <Label>CPD History</Label>
                  <Textarea placeholder="Continuing professional development..." className="min-h-[60px]" value={form.cpd_history} onChange={(e) => updateForm("cpd_history", e.target.value)} data-testid="coaching-cpd" />
                </div>
                <Button className="w-full bg-[#003087] text-white hover:bg-[#002366]" onClick={handleGenerate} disabled={loading} data-testid="coaching-generate-btn">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating Plan...</> : <><TrendingUp className="w-4 h-4 mr-2" /> Generate Coaching Plan</>}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Results */}
          <div className="space-y-4">
            {loading && (
              <Card className="bg-white border border-slate-200 shadow-sm rounded-xl">
                <CardContent className="p-10 text-center">
                  <Loader2 className="w-10 h-10 text-[#003087] animate-spin mx-auto mb-4" />
                  <p className="text-slate-600">Analysing your career progression...</p>
                </CardContent>
              </Card>
            )}

            {result && !result.error && (
              <>
                <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="coaching-gap-analysis">
                  <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Target className="w-4 h-4 text-[#003087]" /> Gap Analysis</CardTitle></CardHeader>
                  <CardContent><p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{result.gap_analysis}</p></CardContent>
                </Card>

                {result.missing_competencies?.length > 0 && (
                  <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="coaching-competencies">
                    <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[#FFB81C]" /> Missing Competencies</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                      {result.missing_competencies.map((c, i) => (
                        <div key={i} className="p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-slate-900">{c.competency}</span>
                            <Badge className={c.priority === "high" ? "bg-red-100 text-red-700" : c.priority === "medium" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}>{c.priority}</Badge>
                          </div>
                          <p className="text-sm text-slate-600">{c.description}</p>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                <Card className="bg-white border border-slate-200 shadow-sm rounded-xl">
                  <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#007F3B]" /> Leadership Assessment</CardTitle></CardHeader>
                  <CardContent><p className="text-sm text-slate-700 whitespace-pre-wrap">{result.leadership_assessment}</p></CardContent>
                </Card>

                {result.roadmap?.length > 0 && (
                  <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="coaching-roadmap">
                    <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><MapPin className="w-4 h-4 text-[#00A3A6]" /> Career Roadmap</CardTitle></CardHeader>
                    <CardContent className="space-y-4">
                      {result.roadmap.map((phase, i) => (
                        <div key={i} className="relative pl-6 border-l-2 border-[#00A3A6] pb-4 last:pb-0">
                          <div className="absolute -left-[7px] top-0 w-3 h-3 rounded-full bg-[#00A3A6]" />
                          <h4 className="text-sm font-semibold text-slate-900 mb-1">{phase.month}</h4>
                          <ul className="space-y-1">
                            {phase.actions.map((a, j) => (
                              <li key={j} className="text-sm text-slate-600 flex items-start gap-1.5">
                                <CheckCircle className="w-3 h-3 text-[#00A3A6] mt-0.5 flex-shrink-0" /> {a}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {result.portfolio_checklist?.length > 0 && (
                  <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="coaching-portfolio">
                    <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><ClipboardList className="w-4 h-4 text-[#003087]" /> Portfolio Checklist</CardTitle></CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {result.portfolio_checklist.map((item, i) => (
                          <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                            <div className="w-4 h-4 border-2 border-slate-300 rounded mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {!result && !loading && (
              <Card className="bg-white border border-slate-200 shadow-sm rounded-xl">
                <CardContent className="p-10 text-center">
                  <TrendingUp className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Fill in your details and generate a personalised coaching plan.</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
